import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useNotification } from '@/components/NotificationProvider';
import { usePermissions } from '@/components/PermissionsProvider';
import { Plus, MoreHorizontal, Pencil, Trash2, DollarSign, Truck, Users, BarChart3, AlertTriangle, RotateCcw, X, Gauge } from 'lucide-react';
import { formatDate, formatDateForInput, formatDisplayDate } from '@/utils/dateUtils';
import ProjectDashboard from '@/components/ProjectDashboard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function getAuthHeader() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

export default function Projects({ user }) {
  const notification = useNotification();
  const { canEdit: canEditModule } = usePermissions();
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [roles, setRoles] = useState([]);
  const [rotations, setRotations] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [activeTab, setActiveTab] = useState('general');
  const [resourcesData, setResourcesData] = useState(null);
  const [loadingResources, setLoadingResources] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    status: 'Secured',
    stage: '',
    entity: '',
    label_color: '',
    is_active: true,
    start_date: '',
    end_date: '',
    description: '',
    extensions: [],
    total_budget: '',
    equipment_budget: '',
    labour_budget: '',
    other_budget: ''
  });

  const [projectStages, setProjectStages] = useState([]);
  const [projectStatuses, setProjectStatuses] = useState([]);
  const [entities, setEntities] = useState([]);
  const [entityFilter, setEntityFilter] = useState('all');

  // Equipment Forecasts
  const [equipmentForecasts, setEquipmentForecasts] = useState([]);
  const [eqForecastForm, setEqForecastForm] = useState({ id: '', category: '', subcategory: '', quantity: 1, rate_per_day: '', start_date: '', end_date: '', notes: '' });
  const [editingEqForecast, setEditingEqForecast] = useState(null);
  const [showEqForecastForm, setShowEqForecastForm] = useState(false);

  // Personnel Forecasts
  const [personnelForecasts, setPersonnelForecasts] = useState([]);
  const [persForecastForm, setPersForecastForm] = useState({ id: '', role: '', headcount: 1, rate_per_day: '', rotation: '', start_date: '', end_date: '', notes: '' });
  const [editingPersForecast, setEditingPersForecast] = useState(null);
  const [showPersForecastForm, setShowPersForecastForm] = useState(false);

  // Bulk select & trash (mirrors Equipment / Personnel)
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [dashboardProjectId, setDashboardProjectId] = useState(null);
  const [showTrash, setShowTrash] = useState(false);
  const [trashedProjects, setTrashedProjects] = useState([]);

  useEffect(() => {
    fetchProjects();
    fetchTrashedProjects();
    fetchCategories();
    fetchRoles();
    fetchRotations();
    fetchProjectStages();
    fetchProjectStatuses();
    fetchEntities();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`, { headers: getAuthHeader() });
      setProjects(response.data);
    } catch (error) {
      notification.error('Failed to load projects');
    }
  };

  const fetchCategories = async () => {
    try {
      const [catRes, subcatRes] = await Promise.all([
        axios.get(`${API}/categories?type=equipment_category`, { headers: getAuthHeader() }),
        axios.get(`${API}/categories?type=equipment_subcategory`, { headers: getAuthHeader() })
      ]);
      setCategories(catRes.data);
      setSubcategories(subcatRes.data);
    } catch (error) { /* Silently fail */ }
  };

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API}/categories?type=personnel_role`, { headers: getAuthHeader() });
      setRoles(response.data);
    } catch (error) { /* Silently fail */ }
  };

  const fetchRotations = async () => {
    try {
      const response = await axios.get(`${API}/categories?type=rotation`, { headers: getAuthHeader() });
      setRotations(response.data);
    } catch (error) { /* Silently fail */ }
  };

  const fetchProjectStages = async () => {
    try {
      const response = await axios.get(`${API}/categories?type=project_stage`, { headers: getAuthHeader() });
      setProjectStages(response.data);
    } catch (error) { /* Silently fail */ }
  };

  const fetchProjectStatuses = async () => {
    try {
      const response = await axios.get(`${API}/categories?type=project_status`, { headers: getAuthHeader() });
      setProjectStatuses(response.data);
    } catch (error) { /* Silently fail */ }
  };

  const fetchEntities = async () => {
    try {
      const response = await axios.get(`${API}/categories?type=ownership_entity`, { headers: getAuthHeader() });
      setEntities(response.data);
    } catch (error) { /* Silently fail */ }
  };

  const fetchResourcesData = async (projectId) => {
    setLoadingResources(true);
    try {
      const response = await axios.get(`${API}/projects/${projectId}/resources`, { headers: getAuthHeader() });
      setResourcesData(response.data);
    } catch (error) {
      notification.error('Failed to load resources data');
    } finally {
      setLoadingResources(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        name: formData.name,
        status: formData.status,
        stage: formData.stage || null,
        entity: formData.entity || null,
        label_color: formData.label_color || null,
        is_active: formData.is_active,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: new Date(formData.end_date).toISOString(),
        description: formData.description || null,
        extensions: (formData.extensions || [])
          .filter(ext => ext.start_date && ext.end_date)
          .map(ext => ({
            start_date: new Date(ext.start_date).toISOString(),
            end_date: new Date(ext.end_date).toISOString()
          })),
        total_budget: formData.total_budget ? parseFloat(formData.total_budget) : null,
        equipment_budget: formData.equipment_budget ? parseFloat(formData.equipment_budget) : null,
        labour_budget: formData.labour_budget ? parseFloat(formData.labour_budget) : null,
        other_budget: formData.other_budget ? parseFloat(formData.other_budget) : null,
        equipment_forecasts: equipmentForecasts.map(f => ({
          id: f.id || undefined,
          category: f.category,
          subcategory: f.subcategory || null,
          quantity: parseInt(f.quantity) || 1,
          rate_per_day: f.rate_per_day ? parseFloat(f.rate_per_day) : null,
          start_date: f.start_date ? new Date(f.start_date).toISOString() : null,
          end_date: f.end_date ? new Date(f.end_date).toISOString() : null,
          notes: f.notes || null
        })),
        personnel_forecasts: personnelForecasts.map(f => ({
          id: f.id || undefined,
          role: f.role,
          headcount: parseInt(f.headcount) || 1,
          rate_per_day: f.rate_per_day ? parseFloat(f.rate_per_day) : null,
          rotation: f.rotation || null,
          start_date: f.start_date ? new Date(f.start_date).toISOString() : null,
          end_date: f.end_date ? new Date(f.end_date).toISOString() : null,
          notes: f.notes || null
        })),
        version: editingItem?.version || null
      };

      if (editingItem) {
        await axios.put(`${API}/projects/${editingItem.id}`, data, { headers: getAuthHeader() });
        notification.success('Project updated successfully');
      } else {
        await axios.post(`${API}/projects`, data, { headers: getAuthHeader() });
        notification.success('Project created successfully');
      }
      setOpen(false);
      setEditingItem(null);
      resetForm();
      fetchProjects();
    } catch (error) {
      if (error.response?.status === 409) {
        notification.conflict(() => {
          setOpen(false);
          setEditingItem(null);
          resetForm();
          fetchProjects();
        });
      } else {
        notification.error(error.response?.data?.detail || 'Operation failed');
      }
    }
  };

  const handleDelete = (id) => {
    notification.confirm('Move this project to trash?\n\nIt can be restored from the Trash view.', async () => {
      try {
        await axios.delete(`${API}/projects/${id}`, { headers: getAuthHeader() });
        notification.success('Project moved to trash');
        fetchProjects();
        fetchTrashedProjects();
      } catch (error) {
        notification.error('Failed to delete project');
      }
    });
  };

  // ---- Bulk select & trash management (mirrors Equipment) ----
  const fetchTrashedProjects = async () => {
    try {
      const res = await axios.get(`${API}/projects/trash/list`, { headers: getAuthHeader() });
      setTrashedProjects(res.data);
    } catch (error) { /* silently fail */ }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === visibleProjects.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(visibleProjects.map(p => p.id)));
    }
  };

  const visibleProjects = entityFilter === 'all'
    ? projects
    : projects.filter(p => (p.entity || '') === (entityFilter === '__none__' ? '' : entityFilter));

  const handleBulkDelete = () => {
    if (selectedIds.size === 0) return;
    notification.confirm(
      `Move ${selectedIds.size} project${selectedIds.size > 1 ? 's' : ''} to trash?\n\nThey can be restored from the Trash view.`,
      async () => {
        try {
          await axios.post(`${API}/projects/bulk-delete`, { ids: Array.from(selectedIds) }, { headers: getAuthHeader() });
          notification.success(`${selectedIds.size} projects moved to trash`);
          setSelectedIds(new Set());
          fetchProjects();
          fetchTrashedProjects();
        } catch (error) { notification.error('Failed to delete projects'); }
      }
    );
  };

  const handleRestore = (id) => {
    notification.confirm('Restore this project?', async () => {
      try {
        await axios.post(`${API}/projects/${id}/restore`, {}, { headers: getAuthHeader() });
        notification.success('Project restored');
        fetchProjects();
        fetchTrashedProjects();
      } catch (error) { notification.error('Failed to restore'); }
    }, { title: 'Restore Project', confirmLabel: 'Restore', confirmClass: 'bg-emerald-600 hover:bg-emerald-700' });
  };

  const handlePermanentDelete = (id) => {
    notification.confirm(
      'Permanently delete this project?\n\nThis CANNOT be undone.',
      async () => {
        try {
          await axios.delete(`${API}/projects/${id}/permanent`, { headers: getAuthHeader() });
          notification.success('Project permanently deleted');
          fetchTrashedProjects();
        } catch (error) { notification.error('Failed to delete'); }
      },
      { title: 'Permanent Delete', confirmLabel: 'Delete Permanently', confirmClass: 'bg-red-600 hover:bg-red-700' }
    );
  };

  const handleEmptyTrash = () => {
    if (trashedProjects.length === 0) return;
    notification.confirm(
      `Permanently delete all ${trashedProjects.length} project${trashedProjects.length > 1 ? 's' : ''} in trash?\n\nThis CANNOT be undone.`,
      async () => {
        try {
          await axios.post(`${API}/projects/trash/empty`, {}, { headers: getAuthHeader() });
          notification.success('Trash emptied');
          fetchTrashedProjects();
        } catch (error) { notification.error('Failed to empty trash'); }
      },
      { title: 'Empty Trash', confirmLabel: 'Empty Trash', confirmClass: 'bg-red-600 hover:bg-red-700' }
    );
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      status: item.status,
      stage: item.stage || '',
      entity: item.entity || '',
      label_color: item.label_color || '',
      is_active: item.is_active ?? true,
      start_date: formatDateForInput(item.start_date),
      end_date: formatDateForInput(item.end_date),
      description: item.description || '',
      extensions: (() => {
        const list = item.extensions && Array.isArray(item.extensions) && item.extensions.length > 0
          ? item.extensions
          : [
              ...(item.extension_1_start && item.extension_1_end ? [{ start_date: item.extension_1_start, end_date: item.extension_1_end }] : []),
              ...(item.extension_2_start && item.extension_2_end ? [{ start_date: item.extension_2_start, end_date: item.extension_2_end }] : [])
            ];
        return list.map(ext => ({
          start_date: ext.start_date ? formatDateForInput(ext.start_date) : '',
          end_date: ext.end_date ? formatDateForInput(ext.end_date) : ''
        }));
      })(),
      total_budget: item.total_budget?.toString() || '',
      equipment_budget: item.equipment_budget?.toString() || '',
      labour_budget: item.labour_budget?.toString() || '',
      other_budget: item.other_budget?.toString() || ''
    });
    // Load equipment forecasts
    setEquipmentForecasts((item.equipment_forecasts || []).map(f => ({
      id: f.id,
      category: f.category || '',
      subcategory: f.subcategory || '',
      quantity: f.quantity || 1,
      rate_per_day: f.rate_per_day?.toString() || '',
      start_date: formatDateForInput(f.start_date),
      end_date: formatDateForInput(f.end_date),
      notes: f.notes || ''
    })));
    // Load personnel forecasts
    setPersonnelForecasts((item.personnel_forecasts || []).map(f => ({
      id: f.id,
      role: f.role || '',
      headcount: f.headcount || 1,
      rate_per_day: f.rate_per_day?.toString() || '',
      rotation: f.rotation || '',
      start_date: formatDateForInput(f.start_date),
      end_date: formatDateForInput(f.end_date),
      notes: f.notes || ''
    })));
    setActiveTab('general');
    setResourcesData(null);
    setOpen(true);
    // Fetch resources data for existing projects
    if (item.id) {
      fetchResourcesData(item.id);
    }
  };

  const handleRowClick = (item, e) => {
    if (e.target.closest('[data-actions-menu]')) return;
    if (canEdit) handleEdit(item);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      status: 'Secured',
      stage: '',
      entity: '',
      label_color: '',
      is_active: true,
      start_date: '',
      end_date: '',
      description: '',
      extensions: [],
      total_budget: '',
      equipment_budget: '',
      labour_budget: '',
      other_budget: ''
    });
    setEquipmentForecasts([]);
    setEqForecastForm({ id: '', category: '', subcategory: '', quantity: 1, rate_per_day: '', start_date: '', end_date: '', notes: '' });
    setEditingEqForecast(null);
    setShowEqForecastForm(false);
    setPersonnelForecasts([]);
    setPersForecastForm({ id: '', role: '', headcount: 1, rate_per_day: '', rotation: '', start_date: '', end_date: '', notes: '' });
    setEditingPersForecast(null);
    setShowPersForecastForm(false);
    setActiveTab('general');
    setResourcesData(null);
  };

  // Equipment Forecast handlers
  const handleAddEqForecast = () => { setEditingEqForecast(null); setEqForecastForm({ id: '', category: '', subcategory: '', quantity: 1, rate_per_day: '', start_date: '', end_date: '', notes: '' }); setShowEqForecastForm(true); };
  const handleEditEqForecast = (f) => { setEditingEqForecast(f); setEqForecastForm({ ...f }); setShowEqForecastForm(true); };
  const handleDeleteEqForecast = (id) => { setEquipmentForecasts(equipmentForecasts.filter(f => f.id !== id)); };
  const handleSaveEqForecast = () => {
    if (!eqForecastForm.category) { notification.error('Category is required'); return; }
    if (editingEqForecast) { setEquipmentForecasts(equipmentForecasts.map(f => f.id === editingEqForecast.id ? { ...eqForecastForm } : f)); }
    else { setEquipmentForecasts([...equipmentForecasts, { ...eqForecastForm, id: `temp-${Date.now()}` }]); }
    setShowEqForecastForm(false); setEditingEqForecast(null);
  };

  // Personnel Forecast handlers
  const handleAddPersForecast = () => { setEditingPersForecast(null); setPersForecastForm({ id: '', role: '', headcount: 1, rate_per_day: '', rotation: '', start_date: '', end_date: '', notes: '' }); setShowPersForecastForm(true); };
  const handleEditPersForecast = (f) => { setEditingPersForecast(f); setPersForecastForm({ ...f }); setShowPersForecastForm(true); };
  const handleDeletePersForecast = (id) => { setPersonnelForecasts(personnelForecasts.filter(f => f.id !== id)); };
  const handleSavePersForecast = () => {
    if (!persForecastForm.role) { notification.error('Role is required'); return; }
    if (editingPersForecast) { setPersonnelForecasts(personnelForecasts.map(f => f.id === editingPersForecast.id ? { ...persForecastForm } : f)); }
    else { setPersonnelForecasts([...personnelForecasts, { ...persForecastForm, id: `temp-${Date.now()}` }]); }
    setShowPersForecastForm(false); setEditingPersForecast(null);
  };

  const getStatusBadge = (status) => {
    const variants = {
      'Secured': 'bg-emerald-50 text-emerald-700 border-emerald-200',
      'Likely': 'bg-yellow-50 text-yellow-700 border-yellow-200',
      'Prospect': 'bg-blue-50 text-blue-700 border-blue-200'
    };
    return variants[status] || variants.Secured;
  };

  const canEdit = canEditModule('projects_database');

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(value);
  };

  // Calculate totals for forecasts
  const getTotalForecastedEquipment = () => equipmentForecasts.reduce((sum, f) => sum + (parseInt(f.quantity) || 0), 0);
  const getTotalForecastedPersonnel = () => personnelForecasts.reduce((sum, f) => sum + (parseInt(f.headcount) || 0), 0);

  return (
    <div className="p-6 md:p-8 lg:p-12" data-testid="projects-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900">Projects</h1>
          <p className="text-sm text-slate-600 mt-2">Manage projects, budgets and resource forecasts</p>
        </div>
        {canEdit && (
          <Sheet open={open} onOpenChange={(isOpen) => { setOpen(isOpen); if (!isOpen) resetForm(); }}>
            <SheetTrigger asChild>
              <Button onClick={() => { setEditingItem(null); resetForm(); }} data-testid="add-project-button" className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm rounded-md px-6 py-2 font-medium">
                <Plus className="w-4 h-4 mr-2" />Add Project
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-xl lg:max-w-4xl overflow-y-auto" data-testid="project-sheet">
              <SheetHeader>
                <SheetTitle className="text-2xl font-bold tracking-tight text-slate-900">{editingItem ? 'Edit Project' : 'Add Project'}</SheetTitle>
                <SheetDescription>{editingItem ? 'Update project details, budget and resource forecasts' : 'Add a new project with budget and resource requirements'}</SheetDescription>
              </SheetHeader>

              <form id="project-form" onSubmit={handleSubmit}>
              <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
                <TabsList className="grid w-full grid-cols-5 text-xs">
                  <TabsTrigger value="general" data-testid="general-tab">General</TabsTrigger>
                  <TabsTrigger value="budget" data-testid="budget-tab"><DollarSign className="w-3 h-3 mr-1" />Budget</TabsTrigger>
                  <TabsTrigger value="equipment" data-testid="equipment-tab"><Truck className="w-3 h-3 mr-1" />Equipment ({equipmentForecasts.length})</TabsTrigger>
                  <TabsTrigger value="personnel" data-testid="personnel-tab"><Users className="w-3 h-3 mr-1" />Personnel ({personnelForecasts.length})</TabsTrigger>
                  {editingItem && <TabsTrigger value="resources" data-testid="resources-tab"><BarChart3 className="w-3 h-3 mr-1" />Resources</TabsTrigger>}
                </TabsList>

                {/* General Tab */}
                <TabsContent value="general">
                  <div className="py-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="name" className="text-sm font-semibold text-slate-700">Project Name *</Label>
                        <Input id="name" data-testid="project-name-input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., Module Installation Phase 1" required />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="status" className="text-sm font-semibold text-slate-700">Project Status *</Label>
                        <select id="status" data-testid="project-status-select" value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm" required>
                          {projectStatuses.length === 0 && <option value="" disabled>No statuses configured — add in Data Management</option>}
                          {projectStatuses.map(s => (
                            <option key={s.id || s.name} value={s.name}>{s.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="stage" className="text-sm font-semibold text-slate-700">Project Stage</Label>
                        <select id="stage" data-testid="project-stage-select" value={formData.stage} onChange={(e) => setFormData({ ...formData, stage: e.target.value })} className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
                          <option value="">No Stage</option>
                          {projectStages.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="entity" className="text-sm font-semibold text-slate-700">Entity</Label>
                        <select id="entity" data-testid="project-entity-select" value={formData.entity} onChange={(e) => setFormData({ ...formData, entity: e.target.value })} className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
                          <option value="">No Entity</option>
                          {entities.map(en => <option key={en.id || en.name} value={en.name}>{en.name}</option>)}
                        </select>
                        <p className="text-xs text-slate-400">Entity awarded the scope · manage in Data Management → Ownership Entities</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="label_color" className="text-sm font-semibold text-slate-700">Label Color</Label>
                        <div className="flex items-center gap-2">
                          <input type="color" id="label_color" data-testid="project-label-color" value={formData.label_color || '#64748b'} onChange={(e) => setFormData({ ...formData, label_color: e.target.value })} className="h-10 w-14 rounded-md border border-slate-300 cursor-pointer" />
                          <Input value={formData.label_color || ''} onChange={(e) => setFormData({ ...formData, label_color: e.target.value })} placeholder="#hex or empty for default" className="flex-1" />
                          {formData.label_color && (
                            <button type="button" onClick={() => setFormData({ ...formData, label_color: '' })} className="text-xs text-slate-500 hover:text-slate-700 underline">Clear</button>
                          )}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold text-slate-700">Status</Label>
                        <div className="flex items-center h-10">
                          <input type="checkbox" id="is_active" data-testid="project-active-checkbox" checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} className="h-4 w-4 rounded border-slate-300 text-slate-900" />
                          <Label htmlFor="is_active" className="ml-2 text-sm text-slate-600">Active</Label>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="start_date" className="text-sm font-semibold text-slate-700">Start Date *</Label>
                        <Input id="start_date" type="date" data-testid="project-start-date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} required />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="end_date" className="text-sm font-semibold text-slate-700">End Date *</Label>
                        <Input id="end_date" type="date" data-testid="project-end-date" value={formData.end_date} onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} required />
                      </div>
                      
                      {/* Project Extensions (dynamic list) */}
                      <div className="md:col-span-2 pt-2 border-t border-slate-100 flex items-center justify-between">
                        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Project Extensions</span>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setFormData({
                            ...formData,
                            extensions: [...(formData.extensions || []), { start_date: '', end_date: '' }]
                          })}
                          className="h-7 text-xs"
                          data-testid="project-add-extension-btn"
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Add Extension
                        </Button>
                      </div>
                      <div className="md:col-span-2 space-y-2">
                        {(formData.extensions || []).length === 0 && (
                          <p className="text-xs text-slate-400 italic">No extensions yet — click "Add Extension" to add one.</p>
                        )}
                        {(formData.extensions || []).map((ext, idx) => (
                          <div key={idx} className="flex items-end gap-2 p-2 rounded-md bg-slate-50 border border-slate-200" data-testid={`project-extension-row-${idx}`}>
                            <div className="flex-shrink-0 w-12 text-xs font-semibold text-slate-500 pb-2">
                              #{idx + 1}
                            </div>
                            <div className="flex-1 space-y-1">
                              <Label className="text-xs font-semibold text-slate-600">Start Date</Label>
                              <Input
                                type="date"
                                value={ext.start_date || ''}
                                onChange={(e) => {
                                  const next = [...formData.extensions];
                                  next[idx] = { ...next[idx], start_date: e.target.value };
                                  setFormData({ ...formData, extensions: next });
                                }}
                                data-testid={`project-extension-start-${idx}`}
                              />
                            </div>
                            <div className="flex-1 space-y-1">
                              <Label className="text-xs font-semibold text-slate-600">End Date</Label>
                              <Input
                                type="date"
                                value={ext.end_date || ''}
                                onChange={(e) => {
                                  const next = [...formData.extensions];
                                  next[idx] = { ...next[idx], end_date: e.target.value };
                                  setFormData({ ...formData, extensions: next });
                                }}
                                data-testid={`project-extension-end-${idx}`}
                              />
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const next = formData.extensions.filter((_, i) => i !== idx);
                                setFormData({ ...formData, extensions: next });
                              }}
                              className="h-9 w-9 p-0 text-red-600 hover:bg-red-50"
                              title="Remove extension"
                              data-testid={`project-extension-remove-${idx}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        ))}
                      </div>

                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="description" className="text-sm font-semibold text-slate-700">Description</Label>
                        <textarea id="description" data-testid="project-description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="w-full h-20 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm" placeholder="Project description..." />
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* Budget Tab */}
                <TabsContent value="budget">
                  <div className="py-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="total_budget" className="text-sm font-semibold text-slate-700">Total Budget</Label>
                        <Input id="total_budget" type="number" step="0.01" data-testid="total-budget-input" value={formData.total_budget} onChange={(e) => setFormData({ ...formData, total_budget: e.target.value })} placeholder="Total project budget" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="equipment_budget" className="text-sm font-semibold text-slate-700">Equipment Budget</Label>
                        <Input id="equipment_budget" type="number" step="0.01" data-testid="equipment-budget-input" value={formData.equipment_budget} onChange={(e) => setFormData({ ...formData, equipment_budget: e.target.value })} placeholder="Equipment allocation" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="labour_budget" className="text-sm font-semibold text-slate-700">Labour Budget</Label>
                        <Input id="labour_budget" type="number" step="0.01" data-testid="labour-budget-input" value={formData.labour_budget} onChange={(e) => setFormData({ ...formData, labour_budget: e.target.value })} placeholder="Labour allocation" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="other_budget" className="text-sm font-semibold text-slate-700">Other Budget</Label>
                        <Input id="other_budget" type="number" step="0.01" data-testid="other-budget-input" value={formData.other_budget} onChange={(e) => setFormData({ ...formData, other_budget: e.target.value })} placeholder="Other costs" />
                      </div>
                    </div>
                    {formData.total_budget && (
                      <div className="mt-6 p-4 bg-slate-50 rounded-md">
                        <h4 className="text-sm font-semibold text-slate-700 mb-2">Budget Summary</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div><span className="text-slate-500">Total:</span> <span className="font-medium">{formatCurrency(formData.total_budget)}</span></div>
                          <div><span className="text-slate-500">Equipment:</span> <span className="font-medium">{formatCurrency(formData.equipment_budget)}</span></div>
                          <div><span className="text-slate-500">Labour:</span> <span className="font-medium">{formatCurrency(formData.labour_budget)}</span></div>
                          <div><span className="text-slate-500">Other:</span> <span className="font-medium">{formatCurrency(formData.other_budget)}</span></div>
                        </div>
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* Equipment Forecasts Tab */}
                <TabsContent value="equipment">
                  <div className="py-4">
                    <div className="flex justify-between items-center mb-4">
                      <div>
                        <p className="text-sm text-slate-600">Equipment requirements for this project</p>
                        <p className="text-xs text-slate-500">Total forecasted: {getTotalForecastedEquipment()} items</p>
                      </div>
                      <Button type="button" size="sm" onClick={handleAddEqForecast} data-testid="add-eq-forecast-button" className="bg-slate-900 hover:bg-slate-800"><Plus className="w-4 h-4 mr-1" />Add Equipment</Button>
                    </div>
                    {showEqForecastForm && (
                      <Card className="mb-4 border-blue-200 bg-blue-50">
                        <CardContent className="pt-4">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div className="space-y-1"><Label className="text-xs font-semibold">Category *</Label><select value={eqForecastForm.category} onChange={(e) => setEqForecastForm({ ...eqForecastForm, category: e.target.value, subcategory: '' })} className="w-full h-9 rounded-md border border-slate-300 bg-white px-3 py-1 text-sm" data-testid="eq-forecast-category"><option value="">Select Category</option>{categories.map((c) => (<option key={c.id} value={c.name}>{c.name}</option>))}</select></div>
                            <div className="space-y-1"><Label className="text-xs font-semibold">Subcategory</Label><select value={eqForecastForm.subcategory} onChange={(e) => setEqForecastForm({ ...eqForecastForm, subcategory: e.target.value })} className="w-full h-9 rounded-md border border-slate-300 bg-white px-3 py-1 text-sm"><option value="">Select Subcategory</option>{subcategories.filter(s => !eqForecastForm.category || s.parent_category === eqForecastForm.category).map((s) => (<option key={s.id} value={s.name}>{s.name}</option>))}</select></div>
                            <div className="space-y-1"><Label className="text-xs font-semibold">Quantity</Label><Input type="number" min="1" value={eqForecastForm.quantity} onChange={(e) => setEqForecastForm({ ...eqForecastForm, quantity: e.target.value })} data-testid="eq-forecast-quantity" /></div>
                            <div className="space-y-1"><Label className="text-xs font-semibold">Rate/Day</Label><Input type="number" step="0.01" value={eqForecastForm.rate_per_day} onChange={(e) => setEqForecastForm({ ...eqForecastForm, rate_per_day: e.target.value })} placeholder="Daily rate" /></div>
                            <div className="space-y-1"><Label className="text-xs font-semibold">Start Date</Label><Input type="date" value={eqForecastForm.start_date} onChange={(e) => setEqForecastForm({ ...eqForecastForm, start_date: e.target.value })} /></div>
                            <div className="space-y-1"><Label className="text-xs font-semibold">End Date</Label><Input type="date" value={eqForecastForm.end_date} onChange={(e) => setEqForecastForm({ ...eqForecastForm, end_date: e.target.value })} /></div>
                            <div className="space-y-1 md:col-span-3"><Label className="text-xs font-semibold">Notes</Label><Input value={eqForecastForm.notes} onChange={(e) => setEqForecastForm({ ...eqForecastForm, notes: e.target.value })} /></div>
                          </div>
                          <div className="flex justify-end gap-2 mt-3">
                            <Button type="button" variant="outline" size="sm" onClick={() => setShowEqForecastForm(false)}>Cancel</Button>
                            <Button type="button" size="sm" onClick={handleSaveEqForecast} className="bg-blue-500 hover:bg-blue-600" data-testid="save-eq-forecast-button">{editingEqForecast ? 'Update' : 'Add'}</Button>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                    {equipmentForecasts.length === 0 ? (<div className="text-center py-8 text-slate-500"><Truck className="w-10 h-10 mx-auto mb-2 text-slate-300" /><p>No equipment forecasts</p></div>) : (
                      <Table><TableHeader><TableRow><TableHead className="text-xs">Category</TableHead><TableHead className="text-xs">Subcategory</TableHead><TableHead className="text-xs">Qty</TableHead><TableHead className="text-xs">Rate/Day</TableHead><TableHead className="text-xs">Period</TableHead><TableHead className="text-xs w-[80px]">Actions</TableHead></TableRow></TableHeader><TableBody>
                        {equipmentForecasts.map((f) => (
                          <TableRow key={f.id}><TableCell className="text-sm font-medium">{f.category}</TableCell><TableCell className="text-sm">{f.subcategory || '-'}</TableCell><TableCell className="text-sm">{f.quantity}</TableCell><TableCell className="text-sm">{f.rate_per_day ? formatCurrency(f.rate_per_day) : '-'}</TableCell><TableCell className="text-sm text-xs">{f.start_date && f.end_date ? `${formatDisplayDate(f.start_date)} - ${formatDisplayDate(f.end_date)}` : '-'}</TableCell><TableCell><div className="flex gap-1"><Button type="button" variant="ghost" size="sm" onClick={() => handleEditEqForecast(f)} className="h-8 w-8 p-0"><Pencil className="w-3 h-3" /></Button><Button type="button" variant="ghost" size="sm" onClick={() => handleDeleteEqForecast(f.id)} className="h-8 w-8 p-0 text-red-600"><Trash2 className="w-3 h-3" /></Button></div></TableCell></TableRow>
                        ))}
                      </TableBody></Table>
                    )}

                    {/* Actual Allocations (with Override flagging) */}
                    {editingItem && resourcesData && Object.values(resourcesData.actual_equipment || {}).flatMap(c => c.items || []).length > 0 && (
                      <div className="mt-6 border-t border-slate-100 pt-4" data-testid="project-eq-actual-allocs">
                        <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                          <Truck className="w-4 h-4" />Actual Allocations
                          <Badge variant="secondary" className="text-xs">{resourcesData.equipment_allocations_count || 0}</Badge>
                          {Object.values(resourcesData.actual_equipment || {}).flatMap(c => c.items || []).some(i => i.is_override) && (
                            <Badge className="bg-orange-100 text-orange-800 border border-orange-300 text-[10px] ml-1">
                              {Object.values(resourcesData.actual_equipment || {}).flatMap(c => c.items || []).filter(i => i.is_override).length} Override{Object.values(resourcesData.actual_equipment || {}).flatMap(c => c.items || []).filter(i => i.is_override).length !== 1 ? 's' : ''}
                            </Badge>
                          )}
                        </h4>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="text-xs">Equipment</TableHead>
                              <TableHead className="text-xs">Category</TableHead>
                              <TableHead className="text-xs">Period</TableHead>
                              <TableHead className="text-xs">Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {Object.entries(resourcesData.actual_equipment || {}).flatMap(([cat, data]) =>
                              (data.items || []).map((item, idx) => (
                                <TableRow key={`eq-actual-${cat}-${idx}`} className={item.is_override ? 'bg-orange-50/60 border-l-2 border-l-orange-400' : ''}>
                                  <TableCell className="text-sm font-medium">
                                    <div className="flex items-center gap-2">
                                      <span>{item.equipment_name || item.internal_tag || (item.id ? item.id.slice(0, 8) : 'Unknown')}</span>
                                      {item.is_override && (
                                        <Badge className="bg-orange-100 text-orange-800 border border-orange-300 text-[10px] px-1.5 py-0 font-semibold">
                                          <AlertTriangle className="w-2.5 h-2.5 mr-0.5" />Override
                                        </Badge>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-sm">{cat}{item.subcategory ? ` / ${item.subcategory}` : ''}</TableCell>
                                  <TableCell className="text-xs">{formatDisplayDate(item.start_date)} – {formatDisplayDate(item.end_date)}</TableCell>
                                  <TableCell className="text-xs capitalize">{item.status}</TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                        <p className="text-xs text-slate-500 mt-2 italic">Override allocations were created despite the equipment category not being listed in the project's forecast requirements.</p>
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* Personnel Forecasts Tab */}
                <TabsContent value="personnel">
                  <div className="py-4">
                    <div className="flex justify-between items-center mb-4">
                      <div>
                        <p className="text-sm text-slate-600">Personnel requirements for this project</p>
                        <p className="text-xs text-slate-500">Total forecasted: {getTotalForecastedPersonnel()} headcount</p>
                      </div>
                      <Button type="button" size="sm" onClick={handleAddPersForecast} data-testid="add-pers-forecast-button" className="bg-slate-900 hover:bg-slate-800"><Plus className="w-4 h-4 mr-1" />Add Personnel</Button>
                    </div>
                    {showPersForecastForm && (
                      <Card className="mb-4 border-emerald-200 bg-emerald-50">
                        <CardContent className="pt-4">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div className="space-y-1"><Label className="text-xs font-semibold">Role *</Label><select value={persForecastForm.role} onChange={(e) => setPersForecastForm({ ...persForecastForm, role: e.target.value })} className="w-full h-9 rounded-md border border-slate-300 bg-white px-3 py-1 text-sm" data-testid="pers-forecast-role"><option value="">Select Role</option>{roles.map((r) => (<option key={r.id} value={r.name}>{r.name}</option>))}</select></div>
                            <div className="space-y-1"><Label className="text-xs font-semibold">Headcount</Label><Input type="number" min="1" value={persForecastForm.headcount} onChange={(e) => setPersForecastForm({ ...persForecastForm, headcount: e.target.value })} data-testid="pers-forecast-headcount" /></div>
                            <div className="space-y-1"><Label className="text-xs font-semibold">Rate/Day</Label><Input type="number" step="0.01" value={persForecastForm.rate_per_day} onChange={(e) => setPersForecastForm({ ...persForecastForm, rate_per_day: e.target.value })} placeholder="Daily rate" /></div>
                            <div className="space-y-1"><Label className="text-xs font-semibold">Rotation</Label><select value={persForecastForm.rotation} onChange={(e) => setPersForecastForm({ ...persForecastForm, rotation: e.target.value })} className="w-full h-9 rounded-md border border-slate-300 bg-white px-3 py-1 text-sm"><option value="">Select Rotation</option>{rotations.map((r) => (<option key={r.id} value={r.name}>{r.name}</option>))}</select></div>
                            <div className="space-y-1"><Label className="text-xs font-semibold">Start Date</Label><Input type="date" value={persForecastForm.start_date} onChange={(e) => setPersForecastForm({ ...persForecastForm, start_date: e.target.value })} /></div>
                            <div className="space-y-1"><Label className="text-xs font-semibold">End Date</Label><Input type="date" value={persForecastForm.end_date} onChange={(e) => setPersForecastForm({ ...persForecastForm, end_date: e.target.value })} /></div>
                            <div className="space-y-1 md:col-span-3"><Label className="text-xs font-semibold">Notes</Label><Input value={persForecastForm.notes} onChange={(e) => setPersForecastForm({ ...persForecastForm, notes: e.target.value })} /></div>
                          </div>
                          <div className="flex justify-end gap-2 mt-3">
                            <Button type="button" variant="outline" size="sm" onClick={() => setShowPersForecastForm(false)}>Cancel</Button>
                            <Button type="button" size="sm" onClick={handleSavePersForecast} className="bg-emerald-500 hover:bg-emerald-600" data-testid="save-pers-forecast-button">{editingPersForecast ? 'Update' : 'Add'}</Button>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                    {personnelForecasts.length === 0 ? (<div className="text-center py-8 text-slate-500"><Users className="w-10 h-10 mx-auto mb-2 text-slate-300" /><p>No personnel forecasts</p></div>) : (
                      <Table><TableHeader><TableRow><TableHead className="text-xs">Role</TableHead><TableHead className="text-xs">Headcount</TableHead><TableHead className="text-xs">Rate/Day</TableHead><TableHead className="text-xs">Rotation</TableHead><TableHead className="text-xs">Period</TableHead><TableHead className="text-xs w-[80px]">Actions</TableHead></TableRow></TableHeader><TableBody>
                        {personnelForecasts.map((f) => (
                          <TableRow key={f.id}><TableCell className="text-sm font-medium">{f.role}</TableCell><TableCell className="text-sm">{f.headcount}</TableCell><TableCell className="text-sm">{f.rate_per_day ? formatCurrency(f.rate_per_day) : '-'}</TableCell><TableCell className="text-sm">{f.rotation || '-'}</TableCell><TableCell className="text-sm text-xs">{f.start_date && f.end_date ? `${formatDisplayDate(f.start_date)} - ${formatDisplayDate(f.end_date)}` : '-'}</TableCell><TableCell><div className="flex gap-1"><Button type="button" variant="ghost" size="sm" onClick={() => handleEditPersForecast(f)} className="h-8 w-8 p-0"><Pencil className="w-3 h-3" /></Button><Button type="button" variant="ghost" size="sm" onClick={() => handleDeletePersForecast(f.id)} className="h-8 w-8 p-0 text-red-600"><Trash2 className="w-3 h-3" /></Button></div></TableCell></TableRow>
                        ))}
                      </TableBody></Table>
                    )}

                    {/* Actual Allocations (with Override flagging) */}
                    {editingItem && resourcesData && Object.values(resourcesData.actual_personnel || {}).flatMap(c => c.items || []).length > 0 && (
                      <div className="mt-6 border-t border-slate-100 pt-4" data-testid="project-pers-actual-allocs">
                        <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                          <Users className="w-4 h-4" />Actual Allocations
                          <Badge variant="secondary" className="text-xs">{resourcesData.personnel_allocations_count || 0}</Badge>
                          {Object.values(resourcesData.actual_personnel || {}).flatMap(c => c.items || []).some(i => i.is_override) && (
                            <Badge className="bg-orange-100 text-orange-800 border border-orange-300 text-[10px] ml-1">
                              {Object.values(resourcesData.actual_personnel || {}).flatMap(c => c.items || []).filter(i => i.is_override).length} Override{Object.values(resourcesData.actual_personnel || {}).flatMap(c => c.items || []).filter(i => i.is_override).length !== 1 ? 's' : ''}
                            </Badge>
                          )}
                        </h4>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="text-xs">Person</TableHead>
                              <TableHead className="text-xs">Role</TableHead>
                              <TableHead className="text-xs">Period</TableHead>
                              <TableHead className="text-xs">Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {Object.entries(resourcesData.actual_personnel || {}).flatMap(([role, data]) =>
                              (data.items || []).map((item, idx) => (
                                <TableRow key={`pers-actual-${role}-${idx}`} className={item.is_override ? 'bg-orange-50/60 border-l-2 border-l-orange-400' : ''}>
                                  <TableCell className="text-sm font-medium">
                                    <div className="flex items-center gap-2">
                                      <span>{item.name || (item.id ? item.id.slice(0, 8) : 'Unknown')}</span>
                                      {item.is_override && (
                                        <Badge className="bg-orange-100 text-orange-800 border border-orange-300 text-[10px] px-1.5 py-0 font-semibold">
                                          <AlertTriangle className="w-2.5 h-2.5 mr-0.5" />Override
                                        </Badge>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-sm">{item.role || role}</TableCell>
                                  <TableCell className="text-xs">{formatDisplayDate(item.start_date)} – {formatDisplayDate(item.end_date)}</TableCell>
                                  <TableCell className="text-xs capitalize">{item.status}</TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                        <p className="text-xs text-slate-500 mt-2 italic">Override allocations were created despite the person's role not being listed in the project's forecast requirements.</p>
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* Resources Tab (Forecast vs Actual) - only for existing projects */}
                {editingItem && (
                  <TabsContent value="resources">
                    <div className="py-4">
                      {loadingResources ? (
                        <div className="text-center py-8 text-slate-500">Loading resources...</div>
                      ) : resourcesData ? (
                        <div className="space-y-6">
                          {/* Budget Summary */}
                          <div className="p-4 bg-slate-50 rounded-md">
                            <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center"><DollarSign className="w-4 h-4 mr-1" />Budget Summary</h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div><span className="text-slate-500 block">Total</span><span className="font-medium text-lg">{formatCurrency(resourcesData.budget?.total)}</span></div>
                              <div><span className="text-slate-500 block">Equipment</span><span className="font-medium">{formatCurrency(resourcesData.budget?.equipment)}</span></div>
                              <div><span className="text-slate-500 block">Labour</span><span className="font-medium">{formatCurrency(resourcesData.budget?.labour)}</span></div>
                              <div><span className="text-slate-500 block">Other</span><span className="font-medium">{formatCurrency(resourcesData.budget?.other)}</span></div>
                            </div>
                          </div>

                          {/* Equipment: Forecast vs Actual */}
                          <div>
                            <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center"><Truck className="w-4 h-4 mr-1" />Equipment: Forecast vs Actual</h4>
                            <Table>
                              <TableHeader><TableRow><TableHead className="text-xs">Category</TableHead><TableHead className="text-xs text-center">Forecast</TableHead><TableHead className="text-xs text-center">Actual</TableHead><TableHead className="text-xs text-center">Variance</TableHead></TableRow></TableHeader>
                              <TableBody>
                                {resourcesData.equipment_forecasts?.map((forecast, idx) => {
                                  const actual = resourcesData.actual_equipment?.[forecast.category]?.count || 0;
                                  const variance = actual - (forecast.quantity || 0);
                                  return (
                                    <TableRow key={idx}>
                                      <TableCell className="text-sm font-medium">{forecast.category}{forecast.subcategory && <span className="text-slate-500 text-xs ml-1">/ {forecast.subcategory}</span>}</TableCell>
                                      <TableCell className="text-center">{forecast.quantity || 0}</TableCell>
                                      <TableCell className="text-center">{actual}</TableCell>
                                      <TableCell className="text-center"><Badge className={variance < 0 ? 'bg-red-50 text-red-700' : variance > 0 ? 'bg-yellow-50 text-yellow-700' : 'bg-emerald-50 text-emerald-700'}>{variance > 0 ? '+' : ''}{variance}</Badge></TableCell>
                                    </TableRow>
                                  );
                                })}
                                {(!resourcesData.equipment_forecasts || resourcesData.equipment_forecasts.length === 0) && (
                                  <TableRow><TableCell colSpan={4} className="text-center text-slate-500 text-sm">No equipment forecasts</TableCell></TableRow>
                                )}
                              </TableBody>
                            </Table>
                            <p className="text-xs text-slate-500 mt-2">Actual allocated: {resourcesData.equipment_allocations_count || 0} items</p>
                          </div>

                          {/* Personnel: Forecast vs Actual */}
                          <div>
                            <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center"><Users className="w-4 h-4 mr-1" />Personnel: Forecast vs Actual</h4>
                            <Table>
                              <TableHeader><TableRow><TableHead className="text-xs">Role</TableHead><TableHead className="text-xs text-center">Forecast</TableHead><TableHead className="text-xs text-center">Actual</TableHead><TableHead className="text-xs text-center">Variance</TableHead></TableRow></TableHeader>
                              <TableBody>
                                {resourcesData.personnel_forecasts?.map((forecast, idx) => {
                                  const actual = resourcesData.actual_personnel?.[forecast.role]?.count || 0;
                                  const variance = actual - (forecast.headcount || 0);
                                  return (
                                    <TableRow key={idx}>
                                      <TableCell className="text-sm font-medium">{forecast.role}{forecast.rotation && <span className="text-slate-500 text-xs ml-1">({forecast.rotation})</span>}</TableCell>
                                      <TableCell className="text-center">{forecast.headcount || 0}</TableCell>
                                      <TableCell className="text-center">{actual}</TableCell>
                                      <TableCell className="text-center"><Badge className={variance < 0 ? 'bg-red-50 text-red-700' : variance > 0 ? 'bg-yellow-50 text-yellow-700' : 'bg-emerald-50 text-emerald-700'}>{variance > 0 ? '+' : ''}{variance}</Badge></TableCell>
                                    </TableRow>
                                  );
                                })}
                                {(!resourcesData.personnel_forecasts || resourcesData.personnel_forecasts.length === 0) && (
                                  <TableRow><TableCell colSpan={4} className="text-center text-slate-500 text-sm">No personnel forecasts</TableCell></TableRow>
                                )}
                              </TableBody>
                            </Table>
                            <p className="text-xs text-slate-500 mt-2">Actual allocated: {resourcesData.personnel_allocations_count || 0} people</p>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-8 text-slate-500"><BarChart3 className="w-10 h-10 mx-auto mb-2 text-slate-300" /><p>No resources data available</p></div>
                      )}
                    </div>
                  </TabsContent>
                )}
              </Tabs>
              </form>

              <SheetFooter className="mt-4">
                <SheetClose asChild><Button type="button" variant="outline" data-testid="cancel-button">Cancel</Button></SheetClose>
                <Button type="submit" form="project-form" data-testid="save-project-button" className="bg-slate-900 hover:bg-slate-800">{editingItem ? 'Update' : 'Create'}</Button>
              </SheetFooter>
            </SheetContent>
          </Sheet>
        )}
      </div>

      <Card className="bg-white border-slate-200 shadow-sm" data-testid="projects-table">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CardTitle className="text-lg font-semibold tracking-tight text-slate-700 uppercase">
                {showTrash ? 'Trash' : 'Projects List'}
              </CardTitle>
              {canEdit && (
                <Button
                  variant={showTrash ? "default" : "outline"}
                  size="sm"
                  onClick={() => { setShowTrash(!showTrash); setSelectedIds(new Set()); }}
                  data-testid="projects-toggle-trash-button"
                  className={showTrash ? "bg-red-600 hover:bg-red-700 text-white" : "border-slate-300 text-slate-600"}
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                  Trash {showTrash ? '' : `(${trashedProjects.length})`}
                </Button>
              )}
            </div>
            <span className="text-xs text-slate-500">
              {showTrash ? `${trashedProjects.length} in trash` : `${projects.length} project${projects.length === 1 ? '' : 's'}`}
            </span>
          </div>
          {/* Bulk action bar */}
          {canEdit && !showTrash && selectedIds.size > 0 && (
            <div className="mt-3 flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2" data-testid="projects-bulk-action-bar">
              <span className="text-sm font-medium text-amber-800">{selectedIds.size} selected</span>
              <Button size="sm" variant="destructive" onClick={handleBulkDelete} data-testid="projects-bulk-delete-button" className="h-7">
                <Trash2 className="w-3.5 h-3.5 mr-1" />Move to Trash
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())} className="h-7 text-slate-600">
                Clear Selection
              </Button>
            </div>
          )}
          {showTrash && trashedProjects.length > 0 && canEdit && (
            <div className="mt-3 flex items-center gap-3 bg-red-50 border border-red-200 rounded-lg px-4 py-2">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              <span className="text-sm text-red-700">Projects in trash will remain until permanently deleted.</span>
              <Button size="sm" variant="destructive" onClick={handleEmptyTrash} data-testid="projects-empty-trash-button" className="h-7 ml-auto">
                Empty Trash
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {showTrash ? (
            /* Trash view */
            trashedProjects.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Trash2 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="font-medium">Trash is empty</p>
                <p className="text-xs mt-1">Deleted projects will appear here</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-mono text-xs uppercase">Name</TableHead>
                    <TableHead className="font-mono text-xs uppercase">Status</TableHead>
                    <TableHead className="font-mono text-xs uppercase">Period</TableHead>
                    <TableHead className="font-mono text-xs uppercase">Deleted By</TableHead>
                    <TableHead className="font-mono text-xs uppercase">Deleted At</TableHead>
                    <TableHead className="font-mono text-xs uppercase w-[160px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {trashedProjects.map((item) => (
                    <TableRow key={item.id} className="opacity-70" data-testid={`projects-trash-row-${item.id}`}>
                      <TableCell className="font-medium text-sm">
                        <div className="flex items-center gap-2">
                          {item.label_color && <div className="w-3 h-3 rounded-full flex-shrink-0 border border-slate-200" style={{ backgroundColor: item.label_color }} />}
                          {item.name}
                        </div>
                      </TableCell>
                      <TableCell><Badge className={getStatusBadge(item.status)}>{item.status}</Badge></TableCell>
                      <TableCell className="text-xs text-slate-600">{formatDate(item.start_date)} - {formatDate(item.end_date)}</TableCell>
                      <TableCell className="text-sm text-slate-500">{item.deleted_by || '-'}</TableCell>
                      <TableCell className="text-sm text-slate-500">{item.deleted_at ? formatDisplayDate(item.deleted_at) : '-'}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" onClick={() => handleRestore(item.id)} data-testid={`projects-restore-${item.id}`} className="h-7 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                            <RotateCcw className="w-3 h-3 mr-1" />Restore
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handlePermanentDelete(item.id)} data-testid={`projects-perm-delete-${item.id}`} className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" title="Delete permanently">
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )
          ) : (
          <>
          <div className="flex items-center gap-2 mb-3">
            <Label htmlFor="entity-filter" className="text-xs font-semibold uppercase tracking-wide text-slate-500">Entity</Label>
            <select
              id="entity-filter"
              data-testid="projects-entity-filter"
              value={entityFilter}
              onChange={(e) => { setEntityFilter(e.target.value); setSelectedIds(new Set()); }}
              className="h-9 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm min-w-[200px]"
            >
              <option value="all">All Entities</option>
              {entities.map(en => <option key={en.id || en.name} value={en.name}>{en.name}</option>)}
              <option value="__none__">No Entity</option>
            </select>
            {entityFilter !== 'all' && (
              <button type="button" onClick={() => setEntityFilter('all')} className="text-xs text-slate-500 hover:text-slate-700 underline" data-testid="projects-entity-filter-clear">Clear</button>
            )}
            <span className="text-xs text-slate-400 ml-auto">{visibleProjects.length} of {projects.length}</span>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                {canEdit && (
                  <TableHead className="w-[40px]">
                    <input
                      type="checkbox"
                      data-testid="projects-select-all"
                      checked={visibleProjects.length > 0 && selectedIds.size === visibleProjects.length}
                      onChange={toggleSelectAll}
                      onClick={(e) => e.stopPropagation()}
                      className="h-4 w-4 cursor-pointer"
                    />
                  </TableHead>
                )}
                <TableHead className="font-mono text-xs uppercase">Name</TableHead>
                <TableHead className="font-mono text-xs uppercase">Project Status</TableHead>
                <TableHead className="font-mono text-xs uppercase">Stage</TableHead>
                <TableHead className="font-mono text-xs uppercase">Entity</TableHead>
                <TableHead className="font-mono text-xs uppercase">Status</TableHead>
                <TableHead className="font-mono text-xs uppercase">Budget</TableHead>
                <TableHead className="font-mono text-xs uppercase">Resources</TableHead>
                <TableHead className="font-mono text-xs uppercase">Period</TableHead>
                {canEdit && <TableHead className="font-mono text-xs uppercase w-[60px]">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleProjects.map((item) => (
                <TableRow key={item.id} data-testid={`project-row-${item.id}`} className={`${canEdit ? "cursor-pointer hover:bg-slate-50" : ""} ${selectedIds.has(item.id) ? 'bg-amber-50/40' : ''}`} onClick={(e) => handleRowClick(item, e)}>
                  {canEdit && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        data-testid={`projects-select-${item.id}`}
                        checked={selectedIds.has(item.id)}
                        onChange={() => toggleSelect(item.id)}
                        className="h-4 w-4 cursor-pointer"
                      />
                    </TableCell>
                  )}
                  <TableCell className="font-medium text-sm">
                    <div className="flex items-center gap-2">
                      {item.label_color && <div className="w-3 h-3 rounded-full flex-shrink-0 border border-slate-200" style={{ backgroundColor: item.label_color }} />}
                      {item.name}
                      <Button
                        variant="ghost"
                        size="sm"
                        title="Open equipment dashboard"
                        data-testid={`project-dashboard-btn-${item.id}`}
                        onClick={(e) => { e.stopPropagation(); setDashboardProjectId(item.id); }}
                        className="h-6 w-6 p-0 text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50"
                      >
                        <Gauge className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell><Badge className={getStatusBadge(item.status)}>{item.status}</Badge></TableCell>
                  <TableCell className="text-sm text-slate-600">{item.stage || '-'}</TableCell>
                  <TableCell className="text-sm text-slate-600">{item.entity || '-'}</TableCell>
                  <TableCell><Badge className={item.is_active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-500 border-slate-200'}>{item.is_active ? 'Active' : 'Inactive'}</Badge></TableCell>
                  <TableCell className="text-sm">{item.total_budget ? formatCurrency(item.total_budget) : '-'}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 text-xs">
                      {(item.equipment_forecasts?.length > 0 || item.personnel_forecasts?.length > 0) ? (
                        <>
                          {item.equipment_forecasts?.length > 0 && <span className="flex items-center gap-1"><Truck className="w-3 h-3 text-blue-500" />{item.equipment_forecasts.reduce((s, f) => s + (f.quantity || 0), 0)}</span>}
                          {item.personnel_forecasts?.length > 0 && <span className="flex items-center gap-1"><Users className="w-3 h-3 text-emerald-500" />{item.personnel_forecasts.reduce((s, f) => s + (f.headcount || 0), 0)}</span>}
                        </>
                      ) : <span className="text-slate-400">-</span>}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-xs">{formatDate(item.start_date)} - {formatDate(item.end_date)}</TableCell>
                  {canEdit && (
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild data-actions-menu><Button variant="ghost" className="h-8 w-8 p-0"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setDashboardProjectId(item.id); }}><Gauge className="mr-2 h-4 w-4" />Dashboard</DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleEdit(item); }}><Pencil className="mr-2 h-4 w-4" />Edit</DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }} className="text-red-600"><Trash2 className="mr-2 h-4 w-4" />Delete</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {visibleProjects.length === 0 && <p className="text-center text-slate-500 py-8 text-sm">{projects.length === 0 ? 'No projects found' : 'No projects match the selected entity'}</p>}
          </>
          )}
        </CardContent>
      </Card>

      <ProjectDashboard
        projectId={dashboardProjectId}
        open={!!dashboardProjectId}
        onOpenChange={(o) => { if (!o) setDashboardProjectId(null); }}
      />
    </div>
  );
}
